import Button from "../../common/Button";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/store";
import {
  Board,
  BoardContext,
  boardHasBeenPaidFor,
  pageFailure,
  QuoteState,
  setView,
  updateCustomQuoteRequest,
} from "../../../reducers/uploadedBoard";
import { BoardView } from "../../../utils/models/boardToolSteps";
import { ButtonContainer, ChildrenContainer } from "./ProjectDetailsStyles";
import * as styledElements from "../Quote/QuoteStyles";
import Link from "next/link";
import {
  MY_ACCOUNT,
  SHOPPING_CART,
  UPLOAD_BOARD,
} from "../../../constants/routes";
import React, { useCallback, useContext } from "react";
import { ButtonVariants } from "../../common/Button/constants";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import LoadingOverlay from "../../LoadingOverlay";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import { useShoppingCartSummary } from "../../../utils/hooks/commonly-stored/shoppingCart";
import { useRouter } from "next/router";
import { DEMO_BOARD_SLUG } from "../../../constants/boards";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import useQuoteHistory from "../../../utils/hooks/commonly-stored/uploaded-board/quoteHistory";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";
import useNotifyInstantQuoted from "../../../utils/hooks/commonly-stored/uploaded-board/notifyInstantQuoted";
import { useCustomAlert } from "../../../utils/hooks/alerts";

function QuoteCallToAction() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const isProcessing = useAppSelector(
    (state) =>
      (state.uploadedBoard.boards[boardId] as BoardContext).isProcessing,
  );
  const sendingBoardEvent = useAppSelector(
    (state) =>
      (state.uploadedBoard.boards[boardId] as BoardContext).sendingBoardEvent,
  );
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const router = useRouter();
  const viewOnly = boardHasBeenPaidFor(board) || board.id == DEMO_BOARD_SLUG;
  const { refreshShoppingCartSummary } = useShoppingCartSummary();
  const dispatch = useAppDispatch();
  const errorHandler = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);
  const { sendBoardEvent, loading: sendingQuoteUpdateEvent } =
    useSendBoardEvent(errorHandler);
  const { refreshQuoteHistory } = useQuoteHistory(boardId);
  const { groups, userId } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const isBoardOwner = board.user_id === userId;
  const customAlert = useCustomAlert();
  const errorMessage = () => {
    customAlert("Failed to email customer about instant quote.");
  };

  const successMessage = () => {
    customAlert("Successfully emailed customer about instant quote!");
  };
  const { notifyBoardIsPurchasable } = useNotifyInstantQuoted(
    errorMessage,
    successMessage,
  );
  const addToCart = async () => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.BOARD_ADDED_TO_CART_EVENT,
    });
    refreshQuoteHistory();
    void refreshShoppingCartSummary();
    // TODO: have this be the customer cart if admin and not your board
    void router.push(SHOPPING_CART);
  };

  const removeFromCart = async () => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.BOARD_REMOVED_FROM_CART_EVENT,
    });
    void refreshShoppingCartSummary();
  };

  const cancelCustomQuote = async () => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.CANCEL_CUSTOM_QUOTE_REQUEST,
    });
    if (boardId) {
      dispatch(updateCustomQuoteRequest({ boardId, requested: false }));
    }
  };

  const requestCustomQuote = async () => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.REQUEST_CUSTOM_QUOTE,
    });
    if (boardId) {
      dispatch(updateCustomQuoteRequest({ boardId, requested: true }));
    }
  };

  const getButtons = () => {
    const quoteIsUpdating =
      board.quote.state == QuoteState.UPDATING_QUOTE ||
      isProcessing ||
      sendingBoardEvent;
    const quoteTotalIsSet = board.quote.total !== null;
    if (viewOnly) {
      return (
        <Button
          label="Go to my account"
          centerText
          variant={ButtonVariants.SECONDARY}
          onClick={() => void router.push(MY_ACCOUNT)}
        />
      );
    }
    if (board.quote.state == QuoteState.IN_SHOPPING_CART) {
      return (
        <Button
          label={"Remove from cart"}
          variant={ButtonVariants.SECONDARY}
          centerText
          onClick={() => void removeFromCart()}
          disabled={quoteIsUpdating}
        />
      );
    }
    if (!quoteTotalIsSet) {
      if (board.quote.requested_custom_quote) {
        return (
          <Button
            label={"Cancel custom quote request"}
            variant={ButtonVariants.SECONDARY}
            centerText
            onClick={() => void cancelCustomQuote()}
            disabled={quoteIsUpdating}
          />
        );
      } else {
        return (
          <Button
            label={"Request custom quote"}
            variant={ButtonVariants.PRIMARY}
            centerText
            onClick={() => void requestCustomQuote()}
            disabled={quoteIsUpdating}
          />
        );
      }
    }

    return (
      <ButtonContainer>
        {isAdminOrEngineer && !isBoardOwner && (
          <Button
            label={"Notify customer board has instant quote"}
            variant={ButtonVariants.PRIMARY}
            centerText
            onClick={() => notifyBoardIsPurchasable(boardId)}
            disabled={quoteIsUpdating}
          />
        )}
        <Button
          label={"Accept quote and add to cart"}
          variant={ButtonVariants.PRIMARY}
          centerText
          onClick={() => void addToCart()}
          disabled={quoteIsUpdating || !quoteTotalIsSet}
        />
      </ButtonContainer>
    );
  };

  return (
    <>
      <LoadingOverlay
        loading={sendingQuoteUpdateEvent}
        text={"Processing..."}
      />
      {getButtons()}
    </>
  );
}

export default function ProjectDetailsCallToAction() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const board = useBoardFromContext().board as Board;
  const isDemoBoard = board.id == DEMO_BOARD_SLUG;
  const view = useAppSelector((state) =>
    boardId ? state.uploadedBoard.boards[boardId]?.view : null,
  );

  return (
    <ChildrenContainer>
      {isDemoBoard && (
        <styledElements.DemoBoard>
          This quote is only for demo purposes. Upload your own board{" "}
          <Link href={UPLOAD_BOARD}>here</Link> to get a real quote in minutes.
        </styledElements.DemoBoard>
      )}
      {view == BoardView.BOARD && boardId && (
        <Button
          label={"Go to bill of materials"}
          centerText
          onClick={() => dispatch(setView({ boardId, view: BoardView.BOM }))}
        />
      )}
      {view == BoardView.BOM && boardId && (
        <Button
          label={"Go to quote"}
          centerText
          onClick={() => dispatch(setView({ boardId, view: BoardView.QUOTE }))}
        />
      )}
      {view == BoardView.QUOTE && <QuoteCallToAction />}
    </ChildrenContainer>
  );
}
